import React, { useRef, useState } from 'react';
import classNames from 'classnames';
import './TextTooltip.css';

interface TextTooltipProps {
  content: string;
  children: React.ReactNode;
  className?: string;
  'data-testid'?: string;
}

// Запас под высоту бабла (1-2 строки + паддинги) — если сверху меньше места
// внутри ближайшего скролл-контейнера, открываем тултип вниз вместо вверх.
const OPEN_DOWN_THRESHOLD = 60;

const getScrollParent = (node: HTMLElement | null): HTMLElement | null => {
  let el = node?.parentElement ?? null;
  while (el) {
    if (/(auto|scroll)/.test(window.getComputedStyle(el).overflowY)) return el;
    el = el.parentElement;
  }
  return null;
};

/**
 * Лёгкий CSS-only тултип для обрезанного текста (email, длинные ФИО и т.п.).
 * В @pro-sto/ui нет переиспользуемого компонента под этот случай — только
 * SvgTooltip (для чартов) и HeroUI Tooltip (зависимость только master-admin,
 * в service-admin не используется).
 *
 * Бабл по умолчанию открывается вверх (bottom: 100%). Если строка находится
 * у самого верха ближайшего скролл-контейнера (например первая строка
 * таблицы), открытие вверх уходит за пределы overflow-области и обрезается —
 * в этом случае перед показом переключаемся на открытие вниз.
 */
export const TextTooltip: React.FC<TextTooltipProps> = ({
  content,
  children,
  className,
  'data-testid': dataTestId,
}) => {
  const anchorRef = useRef<HTMLSpanElement>(null);
  const [openDown, setOpenDown] = useState(false);

  const updateDirection = () => {
    const anchor = anchorRef.current;
    if (!anchor) return;
    const scrollParent = getScrollParent(anchor);
    const boundaryTop = scrollParent?.getBoundingClientRect().top ?? 0;
    setOpenDown(anchor.getBoundingClientRect().top - boundaryTop < OPEN_DOWN_THRESHOLD);
  };

  return (
    <span
      ref={anchorRef}
      className={classNames('text-tooltip', className, {
        'text-tooltip_open-down': openDown,
      })}
      onMouseEnter={updateDirection}
      onFocus={updateDirection}
      data-testid={dataTestId}
    >
      {children}
      <span className="text-tooltip__bubble" role="tooltip">
        {content}
      </span>
    </span>
  );
};

export default TextTooltip;
